import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import DemoFooter from "./DemoFooter";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { RefreshCw, Sparkles } from "lucide-react";

const API_BASE = " https://axtelica-api.vercel.app";

const DemoLanding = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    employees: "",
    country: "",
    scheduleDemoFor: "",
    priority: "Medium",
  });

  const [captchaImage,   setCaptchaImage]   = useState("");
  const [captchaAnswer,  setCaptchaAnswer]  = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // ✅ FIX: useRef instead of useState — handleSubmit always reads
  //    the LATEST token. With useState the old token value is trapped
  //    in the closure and sent with the new captcha answer → always fails.
  const captchaTokenRef = useRef("");

  const [errors,      setErrors]      = useState({});
  const [globalError, setGlobalError] = useState("");
  const [loading,     setLoading]     = useState(false);

  // =========================
  // FETCH CAPTCHA
  // =========================
  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    setErrors((prev) => ({ ...prev, captcha: "" }));
    try {
      const res  = await fetch(`${API_BASE}/api/captcha`);
      const data = await res.json();
      if (data.success) {
        setCaptchaImage(data.captcha_image);
        captchaTokenRef.current = data.captcha_token; // ✅ sync write, never stale
      } else {
        toast.error("Failed to load CAPTCHA");
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not reach the server");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
    setGlobalError("");
  };

  // =========================
  // VALIDATION
  // =========================
  const validate = () => {
    let err = {};

    const allEmpty = Object.values(form).every((v) => !v.trim());
    if (allEmpty) {
      setGlobalError("All fields marked with * are mandatory");
      return {};
    }

    if (!form.firstName.trim())    err.firstName    = "First name is required";
    if (!form.lastName.trim())     err.lastName     = "Last name is required";
    if (!form.company.trim())      err.company      = "Company is required";
    if (!form.email.trim()) {
      err.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      err.email = "Invalid email format";
    }
    if (!form.phone.trim()) {
      err.phone = "Phone is required";
    } else if (!/^\d{7,15}$/.test(form.phone)) {
      err.phone = "Invalid phone number";
    }
    if (!form.employees.trim())       err.employees       = "Employees is required";
    if (!form.country.trim())         err.country         = "Country is required";
    if (!form.scheduleDemoFor.trim()) err.scheduleDemoFor = "Please select an option";
    if (!captchaAnswer.trim())        err.captcha         = "Please enter the CAPTCHA code";

    return err;
  };

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError("");

    if (Object.values(form).every((v) => !v.trim())) {
      toast.error("All fields marked with * are mandatory");
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    // ✅ Always the current token — no stale closure issue
    const currentToken = captchaTokenRef.current;
    if (!currentToken) {
      toast.error("CAPTCHA not loaded. Please refresh the code.");
      fetchCaptcha();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/demo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          captchaAnswer: captchaAnswer.trim().toUpperCase(),
          captchaToken:  currentToken,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error?.toLowerCase().includes("captcha")) {
          fetchCaptcha();
        }
        throw new Error(data.error || "Something went wrong");
      }

      toast.success("Demo request sent!");

      setForm({
        firstName: "", lastName: "", company: "", email: "",
        phone: "", employees: "", country: "", scheduleDemoFor: "",
        priority: "Medium"
      });
      setErrors({});
      setGlobalError("");
      setCaptchaAnswer("");
      fetchCaptcha();

    } catch (err) {
      console.error(err);
      setGlobalError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center text-white relative px-4 py-10"
        style={{
          backgroundImage: "url('/assets/book_demo_pagebg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">

          {/* FORM SIDE */}
          <div className="w-full max-w-[520px] mx-auto flex justify-center">
            <div className="bg-white text-black w-full p-4 sm:p-5 rounded-2xl shadow-xl">

              <h1 className="text-left text-black text-xl sm:text-2xl font-semibold mb-3">
                Schedule a demo
              </h1>

              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
              >

                {/* FIRST NAME */}
                <div>
                  
                  <input
                    type="text" name="firstName" placeholder="First Name"
                    value={form.firstName} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>

                {/* LAST NAME */}
                <div>
                  
                  <input
                    type="text" name="lastName" placeholder="Last Name"
                    value={form.lastName} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>

                {/* COMPANY */}
                <div>
                  
                  <input
                    type="text" name="company" placeholder="Company"
                    value={form.company} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.company && <p className="text-red-500 text-xs mt-1">{errors.company}</p>}
                </div>

                {/* EMAIL */}
                <div>
                  
                  <input
                    type="email" name="email" placeholder="Business Email"
                    value={form.email} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                {/* PHONE */}
                <div>
                  
                  <input
                    type="text" name="phone" placeholder="Phone"
                    value={form.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val.length <= 15) setForm({ ...form, phone: val });
                      setErrors({ ...errors, phone: "" });
                      setGlobalError("");
                    }}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                {/* EMPLOYEES */}
                <div>
                 
                  <input
                    type="text" name="employees" placeholder="Company Size"
                    value={form.employees} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.employees && <p className="text-red-500 text-xs mt-1">{errors.employees}</p>}
                </div>

                {/* COUNTRY */}
                <div className="sm:col-span-2">
                  
                  <input
                    type="text" name="country" placeholder="Country*"
                    value={form.country} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] text-sm"
                  />
                  {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                </div>

                {/* DEMO FOR */}
                <div className="sm:col-span-2">
                  <label className="text-[13px] font-medium block mb-1">Product Interest*</label>
                  <select
                    name="scheduleDemoFor" value={form.scheduleDemoFor} onChange={handleChange}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none bg-white focus:border-[#FF3366] text-sm"
                  >
                    <option value="">Select Option</option>
                    <option value="Axtream">Axtream</option>
                    <option value="Infinisight">Infinisight</option>
                    <option value="AI Agent"> AI Agent</option>
                    <option value="Data & AI">Data & AI</option>
                    <option value="Pricing and Benefits">Pricing and Benefits</option>
                    <option value="Others">Others</option>
                  </select>
                  {errors.scheduleDemoFor && <p className="text-red-500 text-xs mt-1">{errors.scheduleDemoFor}</p>}
                </div>
                {/* PRIORITY */}
<div className="sm:col-span-2">
  <div className="flex items-center gap-5 flex-wrap">
    
    <label className="text-[13px] font-medium">
      Priority*
    </label>

    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="radio"
        name="priority"
        value="Medium"
        checked={form.priority === "Medium"}
        onChange={handleChange}
        className="accent-[#FF3366]"
      />
      Medium
    </label>

    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="radio"
        name="priority"
        value="High"
        checked={form.priority === "High"}
        onChange={handleChange}
        className="accent-[#FF3366]"
      />
      High
    </label>

  </div>

  {errors.priority && (
    <p className="text-red-500 text-xs mt-1">
      {errors.priority}
    </p>
  )}
</div>
                {/* CAPTCHA */}
                <div className="sm:col-span-2">
                  <label className="text-[13px] font-medium block mb-2">Capture*</label>

                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center"
                      style={{ minWidth: 190, height: 65 }}
                    >
                      {captchaLoading ? (
                        <span className="text-gray-400 text-xs">Loading...</span>
                      ) : captchaImage ? (
                        <img
                          src={captchaImage} alt="CAPTCHA"
                          style={{ width: 190, height: 65, display: "block" }}
                          draggable={false}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">Failed to load</span>
                      )}
                    </div>

                    <button
                      type="button" onClick={fetchCaptcha} disabled={captchaLoading}
                      className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-300 hover:border-[#FF3366] hover:text-[#FF3366] transition"
                    >
                      <RefreshCw size={15} className={captchaLoading ? "animate-spin" : ""} />
                    </button>
                  </div>

                  <input
                    type="text"
                    value={captchaAnswer}
                    onChange={(e) => {
                      setCaptchaAnswer(e.target.value.toUpperCase());
                      setErrors((prev) => ({ ...prev, captcha: "" }));
                    }}
                    placeholder="Type the code shown above"
                    maxLength={6}
                    className="w-full border border-gray-300 px-3 py-2 rounded-lg outline-none focus:border-[#FF3366] tracking-widest uppercase text-sm"
                  />
                  {errors.captcha && <p className="text-red-500 text-xs mt-1">{errors.captcha}</p>}
                </div>

                {/* SUBMIT */}
                <div className="sm:col-span-2 mt-1">
                  <button
                    type="submit" disabled={loading}
                    className="w-full bg-[#FF3366] hover:bg-pink-600 transition text-white py-2.5 rounded-full text-sm font-medium disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Book a demo"}
                  </button>

                  {globalError && (
                    <p className="text-red-500 text-sm mt-3 text-center">{globalError}</p>
                  )}
                </div>

              </form>

              <p className="text-[11px] mt-4 text-gray-600 leading-relaxed">
                By clicking Request a Demo, I agree to the use of my
                personal data in accordance with Axtelica Privacy Notice.
              </p>

            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="flex flex-col justify-center text-center lg:text-left px-4">
            <Link to="/">
              <img
                src="/assets/Logo_header_white.png" alt="axtelica"
                className="mb-5 mx-auto lg:mx-0 w-32 sm:w-40"
              />
            </Link>

            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-2 rounded-full mb-5 w-fit">
              <Sparkles size={16} />
              <span className="text-sm">Next Generation AI Security</span>
            </div>

            <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold leading-tight mb-5">
              AI Security
              <span className="bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
                {" "}Built for Scale and Speed
              </span>
            </h1>

            <h3 className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">
              Request a demo with an Axtelica AI product expert to see how you can:
            </h3>

            <ul className="space-y-2 text-base sm:text-lg">
              <li>✔ Connect data from multiple sources</li>
              <li>✔ Ingest batch and real-time data</li>
              <li>✔ Improve data quality and trust</li>
              <li>✔ Detect anomalies and operational risks</li>
            </ul>
          </div>

        </div>
      </motion.section>
      <DemoFooter />
    </>
  );
};

export default DemoLanding;